import { selectBundle, ConsoleLogger, VoidLogger, AsyncDuckDB } from '@duckdb/duckdb-wasm';
import { defu } from 'defu';
import { TZDateMini } from '@date-fns/tz';
import { DataType, TimeUnit, util, Struct } from 'apache-arrow';
import { fromUnixTime, formatDuration as formatDuration$1, format, transpose, addYears, addQuarters, addWeeks, setDay, addMonths, addDays, addHours, addMinutes, addSeconds, addMilliseconds } from 'date-fns';
import { trimEnd } from 'es-toolkit';

function isNullOrUndefined(value) {
  return value === null || value === void 0;
}
function notNullOrUndefined(value) {
  return value !== null && value !== void 0;
}
async function getEnvironment() {
  if (typeof window !== "undefined" && window !== null) {
    return "browser";
  }
  try {
    const process = await import('node:process');
    if (typeof process !== "undefined" && process.versions != null && process.versions.node != null) {
      return "node";
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

var DataFrameCellType = /* @__PURE__ */ ((DataFrameCellType2) => {
  DataFrameCellType2["INDEX"] = "index";
  DataFrameCellType2["DATA"] = "data";
  return DataFrameCellType2;
})(DataFrameCellType || {});
function convertVectorToList(vector) {
  const values = [];
  for (let i = 0; i < vector.length; i++) {
    values.push(vector.get(i));
  }
  return values;
}
function getTimezone(field) {
  return field.type?.timezone ?? field.metadata?.get("timezone");
}
function isIntegerType(field) {
  if (isNullOrUndefined(field)) {
    return false;
  }
  return (
    // Period types are integers with an extra extension name
    DataType.isInt(field.type) && !isPeriodType(field) || isUnsignedIntegerType(field)
  );
}
function isUnsignedIntegerType(field) {
  if (isNullOrUndefined(field)) {
    return false;
  }
  return DataType.isInt(field.type) && field.type.isSigned === false;
}
function isFloatType(field) {
  if (isNullOrUndefined(field)) {
    return false;
  }
  return DataType.isFloat(field.type) ?? false;
}
function isDecimalType(field) {
  if (isNullOrUndefined(field)) {
    return false;
  }
  return DataType.isDecimal(field.type);
}
function isNumericType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return isIntegerType(type) || isFloatType(type) || isDecimalType(type);
}
function isBooleanType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isBool(type.type);
}
function isDurationType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isDuration(type.type);
}
function isPeriodType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isInt(type.type) && type.metadata.get("ARROW:extension:name") === "period";
}
function isDatetimeType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isTimestamp(type.type);
}
function isDateType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isDate(type.type);
}
function isTimeType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isTime(type.type);
}
function isCategoricalType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isDictionary(type.type);
}
function isListType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isList(type.type) || DataType.isFixedSizeList(type.type);
}
function isObjectType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isStruct(type.type) || DataType.isMap(type.type);
}
function isBytesType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isBinary(type.type) || DataType.isLargeBinary(type.type);
}
function isStringType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isUtf8(type.type) || DataType.isLargeUtf8(type.type);
}
function isEmptyType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isNull(type.type);
}
function isIntervalType(type) {
  if (isNullOrUndefined(type)) {
    return false;
  }
  return DataType.isStruct(type.type) && type.metadata.get("ARROW:extension:name") === "interval" || DataType.isInterval(type.type);
}

const BASE_DATE = new Date(1970, 0, 1);
function formatMs(duration) {
  return format(addMilliseconds(BASE_DATE, duration), "yyyy-MM-dd HH:mm:ss.SSS");
}
function formatSec(duration) {
  return format(addSeconds(BASE_DATE, duration), "yyyy-MM-dd HH:mm:ss");
}
function formatMin(duration) {
  return format(addMinutes(BASE_DATE, duration), "yyyy-MM-dd HH:mm");
}
function formatHours(duration) {
  return format(addHours(BASE_DATE, duration), "yyyy-MM-dd HH:mm");
}
function formatDay(duration) {
  return format(addDays(BASE_DATE, duration), "yyyy-MM-dd");
}
function formatMonth(duration) {
  return format(addMonths(BASE_DATE, duration), "yyyy-MM");
}
function formatYear(duration) {
  return format(addYears(BASE_DATE, duration), "yyyy");
}
function formatWeeks(duration, freqParam) {
  if (!freqParam) {
    throw new Error('Frequency "W" requires parameter');
  }
  const WEEKDAY_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const dayIndex = WEEKDAY_SHORT.indexOf(freqParam);
  if (dayIndex < 0) {
    throw new Error(
      `Invalid value: ${freqParam}. Supported values: ${JSON.stringify(
        WEEKDAY_SHORT
      )}`
    );
  }
  const weekDate = addWeeks(BASE_DATE, duration);
  const startDate = format(setDay(weekDate, dayIndex - 6), "yyyy-MM-dd");
  const endDate = format(setDay(weekDate, dayIndex), "yyyy-MM-dd");
  return `${startDate}/${endDate}`;
}
function formatQuarter(duration) {
  const date = addQuarters(BASE_DATE, duration);
  const year = format(date, "yyyy");
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `${year}Q${quarter}`;
}
const PERIOD_TYPE_FORMATTERS = {
  L: formatMs,
  ms: formatMs,
  S: formatSec,
  s: formatSec,
  T: formatMin,
  min: formatMin,
  H: formatHours,
  h: formatHours,
  D: formatDay,
  M: formatMonth,
  W: formatWeeks,
  Q: formatQuarter,
  Y: formatYear,
  A: formatYear
};
function convertTimestampToSeconds(timestamp, unit) {
  let unitAdjustment;
  if (unit === TimeUnit.MILLISECOND) {
    unitAdjustment = 1e3;
  } else if (unit === TimeUnit.MICROSECOND) {
    unitAdjustment = 1e3 * 1e3;
  } else if (unit === TimeUnit.NANOSECOND) {
    unitAdjustment = 1e3 * 1e3 * 1e3;
  } else {
    return Number(timestamp);
  }
  if (typeof timestamp === "bigint" && !Number.isSafeInteger(Number(timestamp))) {
    return Number(timestamp / BigInt(unitAdjustment));
  }
  return Number(timestamp) / unitAdjustment;
}
function convertTimeToDate(timestamp, field) {
  const timeInSeconds = convertTimestampToSeconds(
    timestamp,
    // The default is SECOND because that is the default unit for time values in pandas.
    // Though we believe that actually always a unit is populated by arrow.
    field?.type?.unit ?? TimeUnit.SECOND
  );
  return fromUnixTime(timeInSeconds);
}
function formatDuration(duration, field) {
  return formatDuration$1({
    seconds: convertTimestampToSeconds(
      duration,
      // The default is NANOSECOND because that is the default unit for duration in pandas.
      // Though we believe that actually always a unit is populated by arrow.
      field?.type?.unit ?? TimeUnit.NANOSECOND
    )
  });
}
function formatTime(timestamp, field) {
  const date = convertTimeToDate(timestamp, field);
  return format(
    date,
    date.getMilliseconds() === 0 ? "HH:mm:ss" : "HH:mm:ss.SSS"
  );
}
function formatDate(date) {
  const formatPattern = "yyyy-MM-dd";
  if (!(date instanceof Date || typeof date === "number" && Number.isFinite(date))) {
    console.warn(`Unsupported date value: ${date}`);
    return String(date);
  }
  return format(date, formatPattern);
}
function formatDatetime(date, field) {
  if (!(date instanceof Date || typeof date === "number" && Number.isFinite(date))) {
    console.warn(`Unsupported datetime value: ${date}`);
    return null;
  }
  let datetime;
  const timezone = field?.type?.timezone;
  if (typeof date === "number") {
    if (timezone) {
      datetime = new TZDateMini(date, timezone);
    } else {
      datetime = new TZDateMini(date);
    }
  } else {
    if (timezone) {
      datetime = new TZDateMini(date, timezone);
    } else {
      datetime = new TZDateMini(date);
    }
  }
  return transpose(datetime, Date);
}
function formatDecimal(value, field) {
  const scale = field?.type?.scale || 0;
  let numString = util.bigNumToString(new util.BN(value)).padStart(scale, "0");
  if (scale === 0) {
    return numString;
  }
  let sign = "";
  if (numString.startsWith("-")) {
    sign = "-";
    numString = numString.slice(1);
  }
  const wholePart = numString.slice(0, -scale) || "0";
  const decimalPart = trimEnd(numString.slice(-scale), "0") || "";
  return `${sign}${wholePart}${decimalPart ? `.${decimalPart}` : ""}`;
}
const formatter = new Intl.NumberFormat("en-US", { style: "decimal", maximumFractionDigits: 4, minimumFractionDigits: 4, useGrouping: true });
function formatFloat(num) {
  if (!Number.isFinite(num)) {
    return String(num);
  }
  return formatter.format(num);
}
function parseInterval(arr) {
  const years = arr[0];
  const months = arr[1];
  const result = [];
  if (years !== 0) {
    result.push(`${years} year${years > 1 ? "s" : ""}`);
  }
  if (months !== 0) {
    result.push(`${months} month${months > 1 ? "s" : ""}`);
  }
  return result.length ? result.join(" ") : "0 months";
}
function formatInterval(x, field) {
  if (DataType.isInterval(field?.type)) {
    return parseInterval(x);
  }
  const extensionName = field && field.metadata.get("ARROW:extension:name");
  if (extensionName && extensionName === "pandas.interval") {
    const extensionMetadata = JSON.parse(
      field.metadata.get("ARROW:extension:metadata")
    );
    const { closed } = extensionMetadata;
    const interval = x.toJSON();
    const leftBracket = closed === "both" || closed === "left" ? "[" : "(";
    const rightBracket = closed === "both" || closed === "right" ? "]" : ")";
    const leftInterval = mapColumnData(interval.left, field.type?.children?.[0]);
    const rightInterval = mapColumnData(interval.right, field.type?.children?.[1]);
    return `${leftBracket + leftInterval}, ${rightInterval + rightBracket}`;
  }
  return String(x);
}
function formatPeriodFromFreq(duration, freq) {
  const [freqName, freqParam] = freq.split("-", 2);
  const momentConverter = PERIOD_TYPE_FORMATTERS[freqName];
  if (!momentConverter) {
    console.warn(`Unsupported period frequency: ${freq}`);
    return String(duration);
  }
  const durationNumber = Number(duration);
  if (!Number.isSafeInteger(durationNumber)) {
    console.warn(
      `Unsupported value: ${duration}. Supported values: [${Number.MIN_SAFE_INTEGER}-${Number.MAX_SAFE_INTEGER}]`
    );
    return String(duration);
  }
  return momentConverter(durationNumber, freqParam);
}
function formatPeriod(duration, field) {
  if (isNullOrUndefined(field)) {
    console.warn("Field information is missing");
    return String(duration);
  }
  const extensionName = field.metadata.get("ARROW:extension:name");
  const extensionMetadata = field.metadata.get("ARROW:extension:metadata");
  if (isNullOrUndefined(extensionName) || isNullOrUndefined(extensionMetadata)) {
    console.warn("Arrow extension metadata is missing");
    return String(duration);
  }
  if (extensionName !== "pandas.period") {
    console.warn(`Unsupported extension name for period type: ${extensionName}`);
    return String(duration);
  }
  const parsedExtensionMetadata = JSON.parse(extensionMetadata);
  const { freq } = parsedExtensionMetadata;
  return formatPeriodFromFreq(duration, freq);
}
function formatObject(object, field) {
  if (field?.type instanceof Struct) {
    return JSON.parse(
      JSON.stringify(object, (_key, value) => {
        if (!notNullOrUndefined(value)) {
          return void 0;
        }
        if (typeof value === "bigint") {
          return Number(value);
        }
        return value;
      })
    );
  }
  return JSON.parse(
    JSON.stringify(object, (_key, value) => typeof value === "bigint" ? Number(value) : value)
  );
}
function mapColumnData(x, field) {
  if (isNullOrUndefined(x)) {
    return null;
  }
  const isDate = x instanceof Date || Number.isFinite(x);
  if (isDate && isDateType(field)) {
    return formatDate(x);
  }
  if (typeof x === "bigint" && isTimeType(field)) {
    return formatTime(Number(x), field);
  }
  if (isDate && isDatetimeType(field)) {
    return formatDatetime(x, field);
  }
  if (isPeriodType(field)) {
    throw new Error("Period type is not supported yet");
  }
  if (isIntervalType(field)) {
    return formatInterval(x, field);
  }
  if (isDurationType(field)) {
    throw new Error("Duration type is not supported yet");
  }
  if (isDecimalType(field)) {
    return formatDecimal(x, field);
  }
  if (isFloatType(field) && Number.isFinite(x)) {
    return x;
  }
  if (isIntegerType(field)) {
    return x;
  }
  if (isObjectType(field) || isListType(field)) {
    return formatObject(x, field);
  }
  if (isBooleanType(field)) {
    return Boolean(x);
  }
  return String(x);
}
function mapStructRowData(results) {
  const rows = (results.toArray() || []).map((item) => item.toJSON()) || [];
  const jsRepresentedRows = rows.map((row) => {
    results.schema.fields.forEach((field) => {
      return row[field.name] = mapColumnData(row[field.name], field);
    });
    return row;
  });
  return jsRepresentedRows;
}

var DBStorageType = /* @__PURE__ */ ((DBStorageType2) => {
  DBStorageType2["ORIGIN_PRIVATE_FS"] = "origin-private-fs";
  DBStorageType2["NODE_FS"] = "node-fs";
  return DBStorageType2;
})(DBStorageType || {});

async function connect(options) {
  const opts = defu(options, { logger: false });
  let worker;
  let bundle;
  const env = await getEnvironment();
  if (env === "browser") {
    if (typeof opts.bundles === "undefined") {
      const { getBundles } = await import('./bundles/default-browser.mjs');
      opts.bundles = await getBundles();
    }
    bundle = await selectBundle(await opts.bundles);
    worker = new Worker(bundle.mainWorker);
  } else if (env === "node") {
    if (typeof opts.bundles === "undefined") {
      const { getBundles } = await import('./bundles/default-node.mjs');
      opts.bundles = await getBundles();
    }
    bundle = await selectBundle(await opts.bundles);
    let workerUrl = bundle.mainWorker;
    if (workerUrl.startsWith("/@fs/")) {
      workerUrl = workerUrl.replace("/@fs/", "file://");
    }
    const ww = await import('web-worker');
    worker = new ww.default(workerUrl, { type: "module" });
  } else {
    throw new Error(`Unsupported environment: ${env}`);
  }
  let logger;
  if (opts.logger === true) {
    logger = new ConsoleLogger();
  } else if (opts.logger === false) {
    logger = new VoidLogger();
  } else {
    logger = opts.logger;
  }
  const db = new AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
  if (opts.storage) {
    switch (opts.storage.type) {
      case DBStorageType.ORIGIN_PRIVATE_FS: {
        try {
          let strippedPath = opts.storage.path;
          if (strippedPath.startsWith("/")) {
            strippedPath = strippedPath.slice(1);
          }
          await db.open({
            path: `opfs://${strippedPath}`,
            accessMode: opts.storage.accessMode
            // OPFS already uses direct IO
          });
        } catch (e) {
          await db.terminate();
          await worker.terminate();
          throw e;
        }
        break;
      }
      case DBStorageType.NODE_FS: {
        try {
          await db.open({
            path: opts.storage.path,
            accessMode: opts.storage.accessMode,
            useDirectIO: true
            // Important! Otherwise the file will be created without DB init
          });
        } catch (e) {
          await db.terminate();
          await worker.terminate();
          throw e;
        }
        break;
      }
    }
  }
  const conn = await db.connect();
  return {
    worker,
    db,
    conn,
    query: async (query, params = []) => {
      if (!params || params.length === 0) {
        const results2 = await conn.query(query);
        return mapStructRowData(results2);
      }
      const stmt = await conn.prepare(query);
      const results = await stmt.query(...params);
      const rows = mapStructRowData(results);
      stmt.close();
      return rows;
    },
    close: async () => {
      await conn.close();
      await db.terminate();
      await worker.terminate();
    }
  };
}
async function beginTransaction(client, txFn) {
  await (await client).conn.send("BEGIN TRANSACTION");
  try {
    const result = await txFn(client);
    await (await client).conn.send("COMMIT");
    return result;
  } catch (err) {
    await (await client).conn.send("ROLLBACK");
    throw err;
  }
}
async function withSavepoint(client, spName, txFn) {
  await (await client).conn.send(`SAVEPOINT ${spName}`);
  try {
    const result = await txFn(client);
    await (await client).conn.send(`RELEASE SAVEPOINT ${spName}`);
    return result;
  } catch (err) {
    await (await client).conn.send(`ROLLBACK TO SAVEPOINT ${spName}`);
    throw err;
  }
}

export { DBStorageType, DataFrameCellType, beginTransaction, connect, convertTimeToDate, convertVectorToList, formatDate, formatDatetime, formatDecimal, formatDuration, formatFloat, formatInterval, formatObject, formatPeriod, formatPeriodFromFreq, formatTime, getEnvironment, getTimezone, isBooleanType, isBytesType, isCategoricalType, isDateType, isDatetimeType, isDecimalType, isDurationType, isEmptyType, isFloatType, isIntegerType, isIntervalType, isListType, isNullOrUndefined, isNumericType, isObjectType, isPeriodType, isStringType, isTimeType, isUnsignedIntegerType, mapColumnData, mapStructRowData, notNullOrUndefined, withSavepoint };
